import { Values } from "@/constants";
import { CellProps } from "@/hooks/useContextTree";
import { useOverlayScrollbars } from "overlayscrollbars-react";
import React, { useEffect, useMemo, useRef } from "react";
import { useTreeStore, calculateVisibleIds } from "./Tree.store";
import { TreeRow } from "./TreeRow";

interface TreeLayoutProps {
  contextId: string;
  negativeScrollLeft: any;
  scrollRef: React.MutableRefObject<HTMLDivElement | null>;

  // Column data
  columnsLockedLeft: any[];
  columnsUnlocked: any[];
  columnsLockedRight: any[];

  // Event handlers
  handleScroll: () => void;

  // Cell navigation
  getCellProps: (opts: {
    rowIndex: number;
    colIndex: number;
    objectKey?: string;
  }) => CellProps;
  getClassName: (objectKey: string) => string;
}

export function TreeLayout({
  contextId,
  negativeScrollLeft,
  scrollRef,
  columnsLockedLeft,
  columnsUnlocked,
  columnsLockedRight,
  handleScroll,
  getCellProps,
  getClassName,
}: TreeLayoutProps) {
  const contentDivRef = useRef<HTMLDivElement>(null);
  const layout = useTreeStore((state) => state.layout);
  const visibleRowNodes = useTreeStore((state) => state.visibleRowNodes);
  const visibleStickyRowNodes = useTreeStore(
    (state) => state.visibleStickyRowNodes,
  );
  const treeMap = useTreeStore((state) => state.treeMap);

  // Calculate total height based on ALL visible IDs in tree (not just virtualized window)
  const totalTreeIds = useMemo(() => {
    return calculateVisibleIds(treeMap, layout);
  }, [treeMap, layout]);

  const totalHeight = totalTreeIds.length * Values.DEFAULT_ROW_HEIGHT;

  // Create a map of sticky node IDs to their sticky index (position in sticky stack)
  const stickyNodeMap = useMemo(() => {
    const map = new Map<string, number>();
    visibleStickyRowNodes.forEach((node, idx) => {
      map.set(node.id, idx);
    });
    return map;
  }, [visibleStickyRowNodes]);

  // Merge sticky rows with visible rows, ensuring sticky rows are always rendered
  // even if they're outside the virtualization window
  const mergedRows = useMemo(() => {
    const visibleIds = new Set(visibleRowNodes.map((row) => row.id));
    const result: Array<{
      id: string;
      index: number;
      isSticky: boolean;
      stickyIndex?: number;
    }> = [];

    // First, add sticky rows that are NOT in visibleRowNodes (they've scrolled out of the window)
    visibleStickyRowNodes.forEach((stickyRow, stickyIdx) => {
      if (!visibleIds.has(stickyRow.id)) {
        result.push({
          id: stickyRow.id,
          index: stickyRow.index,
          isSticky: true,
          stickyIndex: stickyIdx,
        });
      }
    });

    // Then add all visible rows, marking sticky ones
    visibleRowNodes.forEach(({ id, index }) => {
      const stickyIndex = stickyNodeMap.get(id);
      result.push({
        id,
        index,
        isSticky: stickyIndex !== undefined,
        stickyIndex,
      });
    });

    return result;
  }, [visibleRowNodes, visibleStickyRowNodes, stickyNodeMap]);

  const rootRef = useRef<HTMLDivElement>(null);
  const subRootRef = useRef<HTMLDivElement>(null);

  const [initialize] = useOverlayScrollbars({
    defer: true,
    options: {
      scrollbars: {},
    },
  });

  useEffect(() => {
    const { current: subRootElement } = subRootRef;
    const { current: rootElement } = rootRef;

    if (subRootElement && rootElement) {
      initialize({
        target: rootElement,
        elements: {
          viewport: subRootElement,
        },
      });
    }
  }, [initialize]);

  return (
    <div
      ref={rootRef}
      style={{
        position: "relative",
        height: "100%",
        width: "100%",
      }}
    >
      <div
        ref={(el) => {
          if (el) {
            subRootRef.current = el;
            scrollRef.current = el;
          }
        }}
        onScroll={handleScroll}
        style={{
          position: "relative",
          height: "100%",
          width: "100%",
          overflowX: "auto",
          overflowY: "auto",
        }}
      >
        {/* Width spacer ensures horizontal scroll works even with few rows.
            The last column's outer edge is drawn 1px past it, so that pixel
            is part of the width: a tree only as wide as its columns clipped it. */}
        <div
          style={{
            width: layout?.hideOuterBorders
              ? `var(--${contextId}-total-width)`
              : `calc(var(--${contextId}-total-width) + 1px)`,
            height: 1,
            marginTop: -1,
            pointerEvents: "none",
          }}
        />
        <div
          ref={contentDivRef}
          style={{
            height: totalHeight,
            width: "100%",
            position: "sticky",
            left: 0,
          }}
        >
          {mergedRows.map(({ id, index, isSticky, stickyIndex }) => (
            <TreeRow
              key={id}
              nodeId={id}
              index={index}
              isLoading={false}
              negativeScrollLeft={negativeScrollLeft}
              columnsLockedLeft={columnsLockedLeft}
              columnsUnlocked={columnsUnlocked}
              columnsLockedRight={columnsLockedRight}
              fetchingOptions={layout.fetchingOptions.row}
              isSticky={isSticky}
              stickyIndex={stickyIndex}
              isLastRow={index === visibleRowNodes.length - 1}
              getCellProps={getCellProps}
              contextId={contextId}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
